// component-product-grouping-styles.js

/* =========================================================
   HELPERS: GROUPING JSON + DEBUG
========================================================= */

function readGroupingJSON() {
  const el = document.querySelector('[id^="ProductGroupingData-"]');
  if (!el) return null;

  try {
    return JSON.parse(el.textContent || "{}");
  } catch (e) {
    console.warn("[Grouping][Debug] Invalid ProductGroupingData JSON", e);
    return null;
  }
}

function logGroupingStats() {
  const data = readGroupingJSON();
  if (!data || !Array.isArray(data.items)) return;

  const items = data.items;
  const byStyle = new Map();

  for (const item of items) {
    const style = (item.style || "NO_STYLE").trim();
    const color = (item.color || "").trim();
    const handle = item.handle;

    if (!byStyle.has(style)) {
      byStyle.set(style, {
        style,
        totalProducts: 0,
        uniqueHandles: new Set(),
        uniqueColors: new Set(),
        sampleHandles: [],
      });
    }

    const entry = byStyle.get(style);
    entry.totalProducts += 1;
    if (handle) entry.uniqueHandles.add(handle);
    if (color) entry.uniqueColors.add(color);
    if (entry.sampleHandles.length < 5 && handle) entry.sampleHandles.push(handle);
  }

  const rows = Array.from(byStyle.values()).map((s) => ({
    style: s.style,
    totalProducts: s.totalProducts,
    uniqueProducts: s.uniqueHandles.size,
    uniqueColors: s.uniqueColors.size,
    sampleHandles: s.sampleHandles.join(", "),
  }));

  console.groupCollapsed(
    `[Grouping][Debug] Stats — currentStyle="${data.currentStyle || ""}", items=${items.length}`
  );
  console.table(rows);
  console.log("[Grouping][Debug] Raw items:", items);
  console.groupEnd();
}

function updateActivePillLabels(root) {
  if (!root) return;

  const groups = root.querySelectorAll(".product-grouping-styles");
  if (!groups.length) return;

  groups.forEach((group) => {
    const activeBtn = group.querySelector(".product-grouping-styles__button.is-active");
    const activeLabelEl = group.querySelector("[data-active-style-label]");
    if (!activeLabelEl) return;

    activeLabelEl.textContent = activeBtn ? (activeBtn.textContent || "").trim() : "";
  });
}

/* =========================================================
   VARIANT SYNC (e.g. Waist + Inseam)
   Goal: ensure form input[name="id"] always matches the UI selections
========================================================= */

/**
 * Reads product JSON from the DOM.
 * You must render it in Liquid inside the product section as:
 * <script type="application/json" data-product-json>{{ product | json }}</script>
 */
function readProductJSON(productSection) {
  if (!productSection) return null;

  const el =
    productSection.querySelector("[data-product-json]") ||
    productSection.querySelector('script[type="application/json"][id^="ProductJson"]') ||
    productSection.querySelector('script[type="application/json"][id^="ProductJSON"]');

  if (!el) return null;

  try {
    return JSON.parse(el.textContent || "{}");
  } catch (e) {
    console.warn("[VariantSync] Invalid product JSON", e);
    return null;
  }
}

function findProductForm(productSection) {
  if (!productSection) return null;

  // Shopify add form usually posts to /cart/add
  return (
    productSection.querySelector('form[action^="/cart/add"]') ||
    productSection.querySelector("form.shopify-product-form")
  );
}

/**
 * Attempts to read selected option values in the correct order (option1/2/3).
 * This supports common themes that mark option groups using data-option-position="1|2|3".
 */
function getSelectedOptions(productSection) {
  const options = [];

  // 1) Best: Shopify option inputs with data-index="option1|option2|option3"
  const optionInputs = productSection.querySelectorAll("[data-index^='option']");
  if (optionInputs && optionInputs.length) {
    for (let pos = 1; pos <= 3; pos++) {
      const selector = `[data-index="option${pos}"]`;
      const checkedRadio = productSection.querySelector(
        `${selector} input[type="radio"]:checked, input[type="radio"]${selector}:checked`
      );

      if (checkedRadio && checkedRadio.value) {
        options[pos - 1] = checkedRadio.value;
        continue;
      }

      const select = productSection.querySelector(`${selector} select`);
      if (select && select.value) {
        options[pos - 1] = select.value;
        continue;
      }

      // Some themes store the attribute directly on the input element:
      const checkedDirect = productSection.querySelector(
        `input[type="radio"][data-index="option${pos}"]:checked`
      );
      if (checkedDirect && checkedDirect.value) {
        options[pos - 1] = checkedDirect.value;
      }
    }

    return options.filter(Boolean);
  }

  // 2) Fallback: wrappers with data-option-position="1|2|3"
  const wrappers = productSection.querySelectorAll("[data-option-position]");
  if (wrappers && wrappers.length) {
    wrappers.forEach((w) => {
      const pos = parseInt(w.getAttribute("data-option-position"), 10);
      if (!pos) return;

      let val = "";
      const checkedRadio = w.querySelector('input[type="radio"]:checked');
      if (checkedRadio) val = checkedRadio.value;

      if (!val) {
        const select = w.querySelector("select");
        if (select) val = select.value;
      }

      if (val) options[pos - 1] = val;
    });

    return options.filter(Boolean);
  }

  // 3) Last fallback (no order guarantee)
  const checked = productSection.querySelectorAll('input[type="radio"]:checked');
  checked.forEach((r) => r?.value && options.push(r.value));
  return options.filter(Boolean);
}

function matchVariantByOptions(productJson, selectedOptions) {
  if (!productJson || !Array.isArray(productJson.variants)) return null;
  if (!selectedOptions || !selectedOptions.length) return null;

  // Exact ordered match for option1/option2/option3
  return (
    productJson.variants.find((v) => {
      const o1 = (v.option1 || "").trim();
      const o2 = (v.option2 || "").trim();
      const o3 = (v.option3 || "").trim();

      const s1 = (selectedOptions[0] || "").trim();
      const s2 = (selectedOptions[1] || "").trim();
      const s3 = (selectedOptions[2] || "").trim();

      if (s1 && o1 !== s1) return false;
      if (selectedOptions.length >= 2 && s2 && o2 !== s2) return false;
      if (selectedOptions.length >= 3 && s3 && o3 !== s3) return false;

      return true;
    }) || null
  );
}

function syncVariantIdToForm(form) {
  if (!form) return;

  const productSection =
    form.closest('[data-section-id]') ||
    form.closest('[data-product]') ||
    form.closest('.product-single__details') ||
    document;

  const productJson = readProductJSON(productSection);
  const selectedOptions = getSelectedOptions(productSection);
  const variant = matchVariantByOptions(productJson, selectedOptions);

  if (!variant) {
    console.warn("[VariantSync] No variant match for", selectedOptions);
    return;
  }

  let idInput = form.querySelector('input[name="id"]');
  if (!idInput) {
    idInput = document.createElement("input");
    idInput.type = "hidden";
    idInput.name = "id";
    form.appendChild(idInput);
  }

  idInput.value = variant.id;

  // also keep the theme's data-product-select in sync
  form.querySelectorAll('input[name="id"][data-product-select]').forEach((el) => (el.value = variant.id));

  // Update the URL with the variant parameter
  updateURLWithVariant(variant.id);

  console.log("[VariantSync][FINAL] writing variant", variant.id, "options:", selectedOptions);
}

function updateURLWithVariant(variantId) {
  if (!variantId) return;
  
  const location = window.location.href;
  if (location.includes("/product")) {
    const url = new window.URL(location);
    const params = url.searchParams;
    params.set("variant", variantId);
    url.search = params.toString();
    const urlString = url.toString();
    window.history.replaceState({ path: urlString }, "", urlString);
    console.log("[VariantSync] Updated URL with variant:", variantId);
  }
}

function rewireExternalFormTargets(oldFormId, newFormId) {
  if (!oldFormId || !newFormId || oldFormId === newFormId) return;

  document
    .querySelectorAll(`[form="${CSS.escape(oldFormId)}"]`)
    .forEach((el) => el.setAttribute("form", newFormId));

  console.log("[ProductGrouping] Rewired external form targets:", oldFormId, "→", newFormId);
}

function bindVariantSync(productSection) {
  if (!productSection) return;

  const form = findProductForm(productSection);
  if (!form) return;

  // 1) Sync on any option UI interaction
  const onMaybeChange = (e) => {
    const t = e.target;
    if (!t) return;

    const isOptionUI =
      t.matches?.('input[type="radio"], select') ||
      t.closest?.("[data-option-position]") ||
      t.closest?.(".product-option") ||
      t.closest?.(".product-options");

    if (!isOptionUI) return;

    // Microtask: let the theme update checked/active state first
    Promise.resolve().then(() => syncVariantIdToForm(form));
  };

  productSection.addEventListener("change", onMaybeChange);
  productSection.addEventListener("click", onMaybeChange);

  // 2) Sync BEFORE submit (capture phase so we run before other handlers)
  form.addEventListener(
    "submit",
    () => {
      syncVariantIdToForm(form);
    },
    true
  );

  // 3) Initial sync on load
  syncVariantIdToForm(form);
}

/* =========================================================
   GLOBAL PRE-SUBMIT SAFETY NET
   Goal: if the submit button is outside the form, still sync variant id
========================================================= */

function bindGlobalPreSubmitSync() {
  if (window.__groupingGlobalPreSubmitBound) return;
  window.__groupingGlobalPreSubmitBound = true;

  document.addEventListener(
    "pointerdown",
    (e) => {
      const submitEl = e.target.closest('button[type="submit"], input[type="submit"]');
      if (!submitEl) return;

      const form =
        submitEl.form ||
        (submitEl.getAttribute("form") && document.getElementById(submitEl.getAttribute("form"))) ||
        submitEl.closest('form[action^="/cart/add"]');

      if (!form) return;

      console.log("[ATC PreSync] Syncing variant before submit (pointerdown)");
      syncVariantIdToSpecificForm(form);
    },
    true
  );

  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action^="/cart/add"], form.product__form')) return;

      console.log("[ATC PreSync] Syncing variant before submit (submit event)");
      syncVariantIdToSpecificForm(form);
    },
    true
  );
  
  // CRITICAL: Intercept both fetch AND XMLHttpRequest to /cart/add
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    
    // Intercept cart add requests
    if (typeof url === 'string' && url.includes('/cart/add')) {
      console.log("[Cart Intercept FETCH] Intercepted cart add");
      const options = args[1] || {};
      
      // If there's a body with form data, check if we need to fix the variant
      if (options.body) {
        try {
          let bodyData;
          
          // Handle different body types
          if (typeof options.body === 'string') {
            // URL-encoded form data or JSON
            if (options.headers && options.headers['Content-Type'] && options.headers['Content-Type'].includes('application/json')) {
              // JSON body
              const data = JSON.parse(options.body);
              console.log("[Cart Intercept FETCH] JSON body variant ID:", data.id);
              console.log("[Cart Intercept FETCH] Full JSON body:", data);
              
              const urlParams = new URLSearchParams(window.location.search);
              const urlVariantId = urlParams.get('variant');
              
              if (urlVariantId && urlVariantId !== String(data.id)) {
                console.warn("[Cart Intercept FETCH] Mismatch! URL:", urlVariantId, "JSON:", data.id);
                console.warn("[Cart Intercept FETCH] FIXING");
                data.id = parseInt(urlVariantId);
                args[1].body = JSON.stringify(data);
              }
            } else {
              // URL-encoded form data
              const params = new URLSearchParams(options.body);
              const variantId = params.get('id');
              console.log("[Cart Intercept FETCH] URLEncoded variant ID:", variantId);
              console.log("[Cart Intercept FETCH] Full URLEncoded body:", options.body);
              
              const urlParams = new URLSearchParams(window.location.search);
              const urlVariantId = urlParams.get('variant');
              
              if (urlVariantId && urlVariantId !== variantId) {
                console.warn("[Cart Intercept FETCH] Mismatch! URL:", urlVariantId, "Form:", variantId);
                console.warn("[Cart Intercept FETCH] FIXING");
                params.set('id', urlVariantId);
                args[1].body = params.toString();
              }
            }
          } else if (options.body instanceof FormData) {
            // Get the URL variant (source of truth)
            const urlParams = new URLSearchParams(window.location.search);
            const urlVariantId = urlParams.get('variant');
            
            // Log all FormData entries BEFORE fix
            const formDataEntries = {};
            for (let [key, value] of options.body.entries()) {
              formDataEntries[key] = value;
            }
            console.log("[Cart Intercept FETCH] FormData BEFORE fix:", formDataEntries);
            
            const isQuickView = options.body.get('section-id') === 'api-quickview';
            if (urlVariantId && !isQuickView) {
              console.warn("[Cart Intercept FETCH] FORCING variant to URL value:", urlVariantId);

              // Delete ALL 'id' entries (FormData can have duplicates!)
              while (options.body.has('id')) {
                options.body.delete('id');
              }

              // Set the ONLY correct id
              options.body.set('id', urlVariantId);
              
              // Log AFTER fix
              const fixedEntries = {};
              for (let [key, value] of options.body.entries()) {
                fixedEntries[key] = value;
              }
              console.log("[Cart Intercept FETCH] FormData AFTER fix:", fixedEntries);
            }
          }
        } catch (e) {
          console.error("[Cart Intercept FETCH] Error:", e);
        }
      }
    }
    
    return originalFetch.apply(this, args);
  };
  
  // Also intercept XMLHttpRequest (older AJAX method)
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;
  
  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._url = url;
    this._method = method;
    return originalXHROpen.apply(this, [method, url, ...rest]);
  };
  
  XMLHttpRequest.prototype.send = function(body) {
    if (this._url && this._url.includes('/cart/add')) {
      console.log("[Cart Intercept XHR] Intercepted cart add");
      
      if (body) {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const urlVariantId = urlParams.get('variant');
          
          if (typeof body === 'string') {
            if (body.startsWith('{')) {
              // JSON
              const data = JSON.parse(body);
              console.log("[Cart Intercept XHR] JSON variant ID:", data.id);
              
              if (urlVariantId && urlVariantId !== String(data.id)) {
                console.warn("[Cart Intercept XHR] Mismatch! URL:", urlVariantId, "JSON:", data.id);
                console.warn("[Cart Intercept XHR] FIXING");
                data.id = parseInt(urlVariantId);
                body = JSON.stringify(data);
              }
            } else {
              // URL-encoded
              const params = new URLSearchParams(body);
              const variantId = params.get('id');
              console.log("[Cart Intercept XHR] URLEncoded variant ID:", variantId);
              
              if (urlVariantId && urlVariantId !== variantId) {
                console.warn("[Cart Intercept XHR] Mismatch! URL:", urlVariantId, "Form:", variantId);
                console.warn("[Cart Intercept XHR] FIXING");
                params.set('id', urlVariantId);
                body = params.toString();
              }
            }
          }
        } catch (e) {
          console.error("[Cart Intercept XHR] Error:", e);
        }
      }
    }
    
    return originalXHRSend.apply(this, [body]);
  };
  
  // CRITICAL: Capture submit event VERY early to prevent theme from overwriting variant ID
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action^="/cart/add"]')) return;

      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        // Lock the variant value by making it read-only momentarily
        const lockedValue = variantInput.value;
        console.log("[ATC Lock] Locking variant ID to:", lockedValue);
        
        // Use a timeout to restore the value if anything tries to change it
        setTimeout(() => {
          if (variantInput.value !== lockedValue) {
            console.warn("[ATC Lock] Variant was changed! Restoring to:", lockedValue);
            variantInput.value = lockedValue;
          }
        }, 0);
      }
    },
    true  // Capture phase - runs before other handlers
  );
}

function syncVariantIdToSpecificForm(form) {
  const scope =
    form.closest('[data-section-id]') ||
    document.querySelector('[data-section-id]') ||
    document;

  const productJson = readProductJSON(scope);
  const selectedOptions = getSelectedOptions(scope);
  const variant = matchVariantByOptions(productJson, selectedOptions);

  if (!variant) return;

  const input = form.querySelector('input[name="id"]');
  if (input) input.value = variant.id;

  form.querySelectorAll('input[name="id"][data-product-select]').forEach((el) => (el.value = variant.id));

  console.log("[ATC FIX] submitting form:", form.id, "variant:", variant.id, "options:", selectedOptions);
}


/* =========================================================
   STYLE SWAP (fetch new product section and replace it safely)
========================================================= */

function replaceProductSectionHTML(productSection, html, sectionId) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Try to find the same product section in the response
  const newSection =
    (sectionId &&
      doc.querySelector(
        `[data-section-type="product-template"][data-section-id="${sectionId}"]`
      )) ||
    doc.querySelector('[data-section-type="product-template"]');

  if (!newSection) {
    console.warn("[ProductGrouping] Could not find product section in fetched HTML.");
    return null;
  }

  // Replace the whole section (more stable than innerHTML)
  productSection.replaceWith(newSection);
  return newSection;
}

function reinitThemeProductForm(sectionEl) {
  if (!sectionEl) return;

  // Kill any existing instance reference (Impact safe)
  sectionEl.querySelectorAll('[data-product-form]').forEach((form) => {
    form.__productAddForm = null;
  });

  // Get the section ID for Product class initialization
  const sectionId = sectionEl.getAttribute('id') || sectionEl.getAttribute('data-section-id');

  // Re-init BOTH Product and ProductAddForm (like color swatches do)
  if (window.Product && sectionId) {
    new Product({ container: document.getElementById(sectionId, true) });
    console.log('[ProductGrouping] Product class reinitialized');
  }
  
  if (window.ProductAddForm && sectionId) {
    new ProductAddForm(document.getElementById(sectionId));
    console.log('[ProductGrouping] ProductAddForm reinitialized');
  }
  
  window.okeWidgetApi?.initAllWidgets()
  new Collapsible(sectionEl);

  // Reinit PDP gallery carousel after section replacement
  if (typeof window.reinitProductGallery === 'function') {
    requestAnimationFrame(function() {
      setTimeout(function() {
        window.reinitProductGallery(sectionEl);
      }, 0);
    });
  }
}

/* =========================================================
   MAIN INIT
========================================================= */

function initProductGrouping(container) {
  const productSection =
    container || document.querySelector('[data-section-type="product-template"]');

  if (!productSection) return;

  const sectionId =
    productSection.getAttribute("data-section-id") || productSection.dataset.sectionId;

  if (!sectionId) {
    console.warn("[ProductGrouping] No data-section-id on product section.");
    return;
  }

  // Bind style buttons
  const styleButtons = productSection.querySelectorAll(".product-grouping-styles__button");

  styleButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.classList.contains("is-active") || btn.disabled) return;

      // Capture BOTH values and option names to restore correctly
      const previousSelection = [];
      
      console.log("[ProductGrouping] CAPTURING selections from current product...");
      
      // Method 1: Try to find checked radios with data-index directly on the input
      for (let i = 1; i <= 3; i++) {
        const optionIndex = `option${i}`;
        let checkedRadio = productSection.querySelector(
          `input[type="radio"][data-index="${optionIndex}"]:checked`
        );
        
        // Method 2: If not found, look for wrapper with data-index
        if (!checkedRadio) {
          const wrapper = productSection.querySelector(`[data-index="${optionIndex}"]`);
          if (wrapper) {
            checkedRadio = wrapper.querySelector('input[type="radio"]:checked');
            console.log(`[ProductGrouping] Found ${optionIndex} via wrapper:`, checkedRadio?.value);
          }
        } else {
          console.log(`[ProductGrouping] Found ${optionIndex} directly:`, checkedRadio?.value);
        }
        
        // Method 3: Try looking for any checked radio in the option group by name
        if (!checkedRadio) {
          const allRadios = productSection.querySelectorAll(`input[type="radio"][data-index="${optionIndex}"]`);
          if (allRadios.length > 0) {
            const radioName = allRadios[0].name;
            checkedRadio = productSection.querySelector(`input[type="radio"][name="${radioName}"]:checked`);
            console.log(`[ProductGrouping] Found ${optionIndex} by name="${radioName}":`, checkedRadio?.value);
          }
        }
        
        if (checkedRadio && checkedRadio.value) {
          previousSelection.push({
            index: optionIndex,
            value: checkedRadio.value
          });
        } else {
          console.warn(`[ProductGrouping] ⚠️ Could not find checked radio for ${optionIndex}`);
        }
      }

      console.log("[ProductGrouping] Captured selection with option indices:", previousSelection);

      const oldForm = findProductForm(productSection);
      const oldFormId = oldForm?.id || "";

      const handle = btn.getAttribute("data-style-handle");
      if (!handle) return;

      // Fetch the new product section HTML
      fetch(`/products/${handle}?section_id=${sectionId}`)
        .then((res) => res.text())
        .then((html) => {
          const newSection = replaceProductSectionHTML(productSection, html, sectionId);
          if (!newSection) return;
          
          const newForm = findProductForm(newSection);
          const newFormId = newForm?.id || "";
          
          // CRITICAL: Find and set the variant BEFORE any theme initialization
          let targetVariantId = null;
          
          if (previousSelection && previousSelection.length > 0) {
            const productJson = readProductJSON(newSection);
            if (productJson) {
              const wantedOptions = ['', '', ''];
              previousSelection.forEach(sel => {
                const idx = parseInt(sel.index.replace('option', '')) - 1;
                wantedOptions[idx] = sel.value;
              });
              
              console.log("[ProductGrouping] Looking for variant matching:", wantedOptions);
              
              const matchedVariant = productJson.variants.find(v => {
                return (
                  (!wantedOptions[0] || v.option1 === wantedOptions[0]) &&
                  (!wantedOptions[1] || v.option2 === wantedOptions[1]) &&
                  (!wantedOptions[2] || v.option3 === wantedOptions[2])
                );
              });
              
              if (matchedVariant) {
                targetVariantId = matchedVariant.id;
                console.log(`[ProductGrouping] ✓ Found matching variant:`, matchedVariant.id, matchedVariant);
                
                // Set ALL hidden inputs with name="id" to this variant
                newSection.querySelectorAll('input[name="id"]').forEach(input => {
                  input.value = matchedVariant.id;
                  console.log(`[ProductGrouping] ✓ Set hidden input to:`, matchedVariant.id);
                });
                
                // Set the radio buttons to match
                previousSelection.forEach((selection) => {
                  const optionIndex = selection.index;
                  const value = selection.value;
                  
                  let input = newSection.querySelector(
                    `input[type="radio"][data-index="${optionIndex}"][value="${CSS.escape(value)}"]`
                  );
                  
                  if (!input) {
                    const wrapper = newSection.querySelector(`[data-index="${optionIndex}"]`);
                    if (wrapper) {
                      input = wrapper.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
                    }
                  }
                  
                  if (input) {
                    // Clear all radios in this group first
                    const radioName = input.name;
                    newSection.querySelectorAll(`input[type="radio"][name="${radioName}"]`).forEach(r => {
                      r.checked = false;
                      r.removeAttribute('checked');
                    });
                    
                    // Set the correct one
                    input.checked = true;
                    input.setAttribute('checked', 'checked');
                    console.log(`[ProductGrouping] ✓ Set ${optionIndex}="${value}" as checked`);
                  }
                });
              } else {
                console.warn("[ProductGrouping] ✗ No matching variant found for:", wantedOptions);
              }
            }
          }
          
          // NOW initialize the theme's ProductAddForm
          reinitThemeProductForm(newSection);

          // Reinit PDP gallery carousel — replaceWith() inserts fresh HTML with no
          // Flickity. Use rAF+setTimeout to match theme's getThreadYielder timing.
          console.log('[PG-DBG] post-replace: reinitProductGallery=', typeof window.reinitProductGallery, 'newSection=', newSection);
          if (typeof window.reinitProductGallery === 'function') {
            requestAnimationFrame(function() {
              setTimeout(function() {
                var slider = newSection.querySelector('[data-product-single-media-slider]');
                var media = newSection.querySelectorAll('[data-product-single-media-wrapper]');
                console.log('[PG-DBG] rAF+timeout: slider=', slider, 'media.length=', media.length);
                window.reinitProductGallery(newSection);
              }, 0);
            });
          }

          rewireExternalFormTargets(oldFormId, newFormId);
          initProductGrouping(newSection);

          // CRITICAL: After theme init, trigger change events to force variant recalculation
          if (targetVariantId && previousSelection && previousSelection.length > 0) {
            requestAnimationFrame(() => {
              console.log("[ProductGrouping] Forcing variant selection after theme init");
              
              // Trigger change event on the LAST option (usually inseam/length)
              // This forces the theme to recalculate the variant based on ALL selections
              const lastSelection = previousSelection[previousSelection.length - 1];
              if (lastSelection) {
                const lastOptionIndex = lastSelection.index;
                const lastValue = lastSelection.value;
                
                let lastInput = newSection.querySelector(
                  `input[type="radio"][data-index="${lastOptionIndex}"][value="${CSS.escape(lastValue)}"]`
                );
                
                if (!lastInput) {
                  const wrapper = newSection.querySelector(`[data-index="${lastOptionIndex}"]`);
                  if (wrapper) {
                    lastInput = wrapper.querySelector(`input[type="radio"][value="${CSS.escape(lastValue)}"]`);
                  }
                }
                
                if (lastInput && lastInput.checked) {
                  console.log(`[ProductGrouping] Triggering change on ${lastOptionIndex}="${lastValue}"`);
                  lastInput.dispatchEvent(new Event('change', { bubbles: true }));
                  lastInput.dispatchEvent(new Event('click', { bubbles: true }));
                }
              }
              
              // Double-check after events
              setTimeout(() => {
                const actuallySelected = getSelectedOptions(newSection);
                const variantInput = newSection.querySelector('input[name="id"]');
                
                console.log("[ProductGrouping] Final check - selections:", actuallySelected, "variant:", variantInput?.value);
                
                // If STILL wrong, force it one more time
                if (targetVariantId && variantInput && variantInput.value !== String(targetVariantId)) {
                  console.warn("[ProductGrouping] ⚠️ Theme STILL has wrong variant! Nuclear option:", targetVariantId);
                  newSection.querySelectorAll('input[name="id"]').forEach(input => {
                    input.value = targetVariantId;
                  });
                  updateURLWithVariant(targetVariantId);
                } else {
                  // Update URL with correct variant
                  const finalVariantId = variantInput?.value;
                  if (finalVariantId) {
                    updateURLWithVariant(finalVariantId);
                  }
                }
              }, 100);
            });
          } else {
            // No previous selection, just update URL
            requestAnimationFrame(() => {
              const variantInput = newSection.querySelector('input[name="id"]');
              if (variantInput?.value) {
                updateURLWithVariant(variantInput.value);
              }
            });
          }
        })
        .catch((err) => console.error("[ProductGrouping] Failed to load style", err));
    });

  });

  // Update "Label: Active Pill" text
  updateActivePillLabels(productSection);

  // Ensure variant id sync works for 2-option products (Waist + Inseam)
  bindVariantSync(productSection);

  // One-time global pre-submit safety net (works for desktop + mobile)
  bindGlobalPreSubmitSync();

  // Initial debug
  logGroupingStats();
}

// Initial load
document.addEventListener("DOMContentLoaded", () => {
  initProductGrouping();
});

// Shopify theme editor section reload
document.addEventListener("theme:section:load", (event) => {
  const sectionId = event.detail?.sectionId;

  const sectionEl = sectionId
    ? document.querySelector(
        `[data-section-type="product-template"][data-section-id="${sectionId}"]`
      )
    : event.target?.querySelector?.('[data-section-type="product-template"]');

  if (sectionEl) initProductGrouping(sectionEl);
});
